// ────────────────────────────────────────────────────────────────────────────
// What an order's cargo IS, as data — the identifying detail a planner or an
// operator needs at a glance, without opening the order.
//
// Shared by the orders list's Info column and the plan cockpit's job queue.
// Pure on purpose: the rules interact (family selects one half, pending
// withholds it, the temperature joins either), and they are worth pinning
// without a DOM.
// ────────────────────────────────────────────────────────────────────────────
import type { CargoDefinition, Order, OrderTask } from '@my/tms-api';
import { productLineLabel } from './allocation';
import { cargoFamilyFor, cargoHandlingPhaseFor } from './cargoPick';

/** One line of the Info cell. A `caption` names the field; the goods line has none. */
export interface CargoInfoRow {
  caption: 'BIC' | 'Tank' | 'Product' | 'Temp' | null;
  value: string;
  /** Hover text, when the value is a shortened form ("Acid +2" → all three). */
  title?: string;
}

/**
 * Whether a row names a physical UNIT — the box or the barrel — rather than
 * what is in it.
 *
 * <p>
 * The plan cockpit splits these out into their own Tank/BIC column, next to the
 * trailer, because to a planner the unit is equipment: it sits with the vehicle
 * and the trailer, not with the goods. The orders list keeps them together,
 * having no equipment block to sit beside. One predicate so the two screens
 * agree on where the line goes.
 * </p>
 */
export function isUnitRow(row: CargoInfoRow): boolean {
  return row.caption === 'BIC' || row.caption === 'Tank';
}

/**
 * What the Info cell says, as data — kept pure so the rules can be pinned
 * without rendering: which half the family selects, what `pending` withholds,
 * and when the temperature joins.
 */
export function cargoInfoRows(
  order: Order,
  /** The order's cargo definition — the family is resolved from it here, so no
   *  caller can pair an order with the wrong one. Undefined while the
   *  definitions are loading, which reads as the goods-and-description family. */
  cargoDef: CargoDefinition | undefined,
  tasks: OrderTask[],
  pending: boolean,
): CargoInfoRow[] {
  const family = cargoFamilyFor(cargoDef?.cargoType);
  const rows: CargoInfoRow[] = [];

  // The cargo rows are still arriving; the temperature is on the ORDER, so only
  // the identifier / goods half has to wait for them.
  if (!pending) {
    if (family === 'container' || family === 'tank') {
      const caption = family === 'tank' ? 'Tank' : 'BIC';
      for (const id of collectUnitIdentifiers(tasks)) rows.push({ caption, value: id });
    } else {
      const goods = summariseCargoFromTasks(tasks);
      if (goods) rows.push({ caption: null, value: goods });
    }

    // What the job carries, for any cargo type with products: the first
    // product, "+N" for the rest, all of them on hover.
    const products = collectOrderProducts(tasks);
    const first = products[0];
    if (first) {
      rows.push({
        caption: 'Product',
        value: products.length > 1 ? `${first} +${products.length - 1}` : first,
        title: products.join(', '),
      });
    }
  }

  // A temperature order is one that HAS a temperature: the column is only
  // populated when the picked cargo definition carries the temperature property
  // group, and the BE requires it there. Reading the definition's groups instead
  // would be the same answer with an extra way to disagree, and would go blank
  // on an order whose definition was edited after the fact.
  if (order.temperatureC != null) {
    rows.push({ caption: 'Temp', value: `${order.temperatureC}°C` });
  }

  return rows;
}

/**
 * Every distinct unit identifier (BIC code / Tank ID) on the order, in the
 * order the tasks run.
 *
 * <p>
 * Read from EVERY task, not just the load-side legs the goods summary uses.
 * Repeating the same box on later stops is the point — the identifier cascades
 * downstream — so deduplication does the work, and a unit that only appears on
 * a delivery leg (rows the allocation reconciler created) is still a box on
 * this job.
 * </p>
 */
export function collectUnitIdentifiers(tasks: OrderTask[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const task of tasks) {
    for (const item of task.cargoItems ?? []) {
      const id = item.identifier?.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
  }
  return out;
}

/**
 * One-line cargo summary from the order's **collected** cargo items — i.e.
 * only the load-side legs (PickUp / Load / Collect). Delivery-side tasks
 * (Deliver / DropOff / Unload) repeat the same goods, so including them
 * would double-count, so they're skipped. Each distinct item renders as
 * "Qty x Description" (e.g. "10 x Euro Pallet, 6 x Documents"); items with
 * no quantity fall back to the bare description. The cell truncates with an
 * ellipsis and surfaces the whole string on hover. Returns null when no
 * collected item carries a description yet.
 */
export function summariseCargoFromTasks(tasks: OrderTask[]): string | null {
  const seen = new Set<string>();
  const parts: string[] = [];
  for (const task of tasks) {
    // Collected (loaded) cargo only — skip the delivery side.
    if (cargoHandlingPhaseFor(task.type) !== 'load') continue;
    for (const item of task.cargoItems ?? []) {
      const desc = item.description?.trim();
      if (!desc) continue;
      const label = item.quantity != null ? `${item.quantity} x ${desc}` : desc;
      if (!seen.has(label)) {
        seen.add(label);
        parts.push(label);
      }
    }
  }
  return parts.length > 0 ? parts.join(', ') : null;
}

/**
 * Every distinct product the order COLLECTS, in task order then compartment
 * order. Load-side legs only — a delivery repeats the products it draws from,
 * so reading it too would only double-count.
 */
export function collectOrderProducts(tasks: OrderTask[]): string[] {
  const out: string[] = [];
  const ordered = tasks.slice().sort((a, b) => a.sequence - b.sequence);
  for (const task of ordered) {
    if (cargoHandlingPhaseFor(task.type) !== 'load') continue;
    const items = (task.cargoItems ?? []).slice().sort((a, b) => a.itemIndex - b.itemIndex);
    for (const item of items) {
      const lines = (item.products ?? []).slice().sort((a, b) => a.sequence - b.sequence);
      for (const line of lines) {
        const label = productLineLabel(line);
        if (!out.includes(label)) out.push(label);
      }
    }
  }
  return out;
}
